import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Container,
  Card,
  CardBody,
  CardFooter,
  Row,
  Col,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import RoleService from "../../../services/KangarooServices/RoleService";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useNavigate, useParams } from "react-router-dom";



const RoleCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [details, setDetails] = useState(null);
  const [menuData, setMenuData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Key mapping: permission name (as string from role API) → numeric enumValue (from menu API)
  const [nameToEnumMap, setNameToEnumMap] = useState({});

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: details?.name || '',
      description: details?.description || '',
      subHeadingAccess: details?.subHeadingAccess || [], // will contain NUMBERS
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Role name is required'),
      description: Yup.string().nullable(),
      subHeadingAccess: Yup.array().of(Yup.number()),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const payload = {
          name: values.name,
          description: values.description,
          subHeadingAccess: values.subHeadingAccess, // already numbers
        };

        if (id) {
          await RoleService.update(id, payload);
          toast.success('Role updated successfully');
        } else {
          await RoleService.create(payload);
          toast.success('Role created successfully');
        }

        resetForm();
        navigate('/roles');
      } catch (error) {
        console.error(error);
        toast.error(id ? 'Failed to update role' : 'Failed to create role');
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Fetch menu structure + build name → enum map
  const fetchRoleMenu = async () => {
    try {
      const response = await RoleService.getRoles();
      setMenuData(response || []);

      const nameToEnum = {};
      response?.forEach((group) => {
        group.subMenus?.forEach((sub) => {
          // Use sub.name as key (matches what getbyId returns)
          if (sub.name && sub.enumValue !== undefined) {
            nameToEnum[sub.name] = sub.enumValue;
          }
        });
      });

      setNameToEnumMap(nameToEnum);
    } catch (error) {
      console.error('Error fetching menu data:', error);
      toast.error('Failed to load menu data');
    }
  };

  // Fetch single role details + convert string names → numbers
  const fetchDetail = async (roleId) => {
    if (!roleId) return;

    try {
      setIsLoading(true);
      const response = await RoleService.getbyId(roleId);

      let subHeadingAccessNumbers = [];

      if (Array.isArray(response.subHeadingAccess)) {
        subHeadingAccessNumbers = response.subHeadingAccess
          .map((name) => nameToEnumMap[name])
          .filter((num) => num !== undefined && !isNaN(num));
      }

      const normalized = {
        ...response,
        subHeadingAccess: subHeadingAccessNumbers,
      };

      setDetails(normalized);
    } catch (error) {
      console.error('Error fetching role details:', error);
      toast.error('Failed to load role details');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRoleMenu();
  }, []);

  useEffect(() => {
    if (id && Object.keys(nameToEnumMap).length > 0) {
      fetchDetail(id);
    } else if (!id) {
      setDetails(null);
      setIsLoading(false);
    }
  }, [id, nameToEnumMap]);

  // ─── Helpers ────────────────────────────────────────────────

  const isGroupFullySelected = (group) => {
    if (!group?.subMenus?.length) return false;
    return group.subMenus.every((sub) =>
      formik.values.subHeadingAccess.includes(sub.enumValue)
    );
  };

  const toggleSubMenu = (enumValue) => {
    const current = formik.values.subHeadingAccess || [];
    const updated = current.includes(enumValue)
      ? current.filter((v) => v !== enumValue)
      : [...current, enumValue];

    formik.setFieldValue('subHeadingAccess', updated);
  };

  const toggleGroup = (group) => {
    if (!group?.subMenus?.length) return;

    const subValues = group.subMenus.map((s) => s.enumValue);
    const allSelected = subValues.every((v) => formik.values.subHeadingAccess.includes(v));

    let updated;
    if (allSelected) {
      updated = formik.values.subHeadingAccess.filter((v) => !subValues.includes(v));
    } else {
      updated = [...new Set([...formik.values.subHeadingAccess, ...subValues])];
    }

    formik.setFieldValue('subHeadingAccess', updated);
  };

  const breadcrumbItems = [{ title: 'Back to List', link: '/roles' }];

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title={id ? 'Edit Role' : 'Create Role'}
          pageTitle="Roles"
          breadcrumbItems={breadcrumbItems}
        />

        <form onSubmit={formik.handleSubmit}>
          <div className="d-flex justify-content-end mb-4 gap-3">
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => navigate('/roles')}
              disabled={formik.isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-success"
              disabled={formik.isSubmitting || isLoading}
            >
              {formik.isSubmitting
                ? 'Processing...'
                : id
                ? 'Update Role'
                : 'Create Role'}
            </button>
          </div>

          <Row>
            {/* Basic Info */}
            <Col lg={6}>
              <Card>
                <CardBody>
                  <FormGroup>
                    <Label for="name">
                      Role Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.name && !!formik.errors.name}
                      disabled={isLoading}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <div className="text-danger mt-1 small">{formik.errors.name}</div>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label for="description">Description</Label>
                    <Input
                      id="description"
                      name="description"
                      type="textarea"
                      rows={5}
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={isLoading}
                    />
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>

            {/* Permissions */}
            <Col lg={6}>
              <Card>
                <CardBody>
                  <h5 className="card-title mb-3">Permissions</h5>

                  <div className="menu-wrapper">
                    {isLoading ? (
                      <div className="text-center py-4 text-muted">Loading permissions...</div>
                    ) : menuData.length === 0 ? (
                      <div className="text-center py-4 text-muted">No permissions available</div>
                    ) : (
                      menuData.map((group) => {
                        const allChecked = isGroupFullySelected(group);

                        return (
                          <div
                            key={group.mainHeadingEnumValue ?? group.mainHeadingName}
                            className="menu-card mb-3"
                          >
                            {group.mainHeadingName && (
                              <div className="form-check mb-3">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`group-${group.mainHeadingEnumValue}`}
                                  checked={allChecked}
                                  onChange={() => toggleGroup(group)}
                                />
                                <label
                                  className="form-check-label fw-bold"
                                  htmlFor={`group-${group.mainHeadingEnumValue}`}
                                >
                                  {group.mainHeadingName}
                                </label>
                              </div>
                            )}

                            <div className="submenu-list ms-4">
                              {group.subMenus?.map((sub) => {
                                const isChecked = formik.values.subHeadingAccess.includes(
                                  sub.enumValue
                                );

                                return (
                                  <div key={sub.enumValue} className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`perm-${sub.enumValue}`}
                                      checked={isChecked}
                                      onChange={() => toggleSubMenu(sub.enumValue)}
                                    />
                                    <label className="form-check-label" htmlFor={`perm-${sub.enumValue}`}>
                                      {sub.name}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </form>
      </div>
    </Container>
  );
};

export default RoleCreate;